import { useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  connectGoogleCalendar,
  syncGoogleCalendarBusy
} from '../../../services/calendarSyncClient';

/**
 * Calendar sync for the venue dashboard. Uses venue contact email by default;
 * pass a string email or { spaceId } from home timeline / settings.
 */
export const useCalendarSync = (venueId, venueDetails, fetchBusyTimes) => {
  const { currentUser } = useAuth();
  const [syncLoading, setSyncLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const resolveSyncEmail = useCallback(() => {
    return (
      venueDetails?.contact_email ||
      venueDetails?.contactEmail ||
      currentUser?.email ||
      ''
    );
  }, [venueDetails, currentUser?.email]);

  const syncCalendar = async (overrideUserIdOrOptions) => {
    if (!venueId) {
      alert('No venue ID found');
      return;
    }

    let overrideUserId = null;
    let spaceId = null;
    if (typeof overrideUserIdOrOptions === 'string') {
      overrideUserId = overrideUserIdOrOptions.trim() || null;
    } else if (overrideUserIdOrOptions && typeof overrideUserIdOrOptions === 'object') {
      overrideUserId =
        overrideUserIdOrOptions.userId ||
        overrideUserIdOrOptions.email ||
        null;
      spaceId = overrideUserIdOrOptions.spaceId || null;
    }

    const ownerEmail = overrideUserId || resolveSyncEmail();
    if (!ownerEmail) {
      alert('No Google account email. Set venue contact email or pick an account in Calendar sync settings.');
      return;
    }

    try {
      setSyncLoading(true);
      let result = await syncGoogleCalendarBusy({
        userId: ownerEmail,
        venueId,
        days: 30,
        spaceId
      });

      if (result.needsAuth) {
        await connectGoogleCalendar(ownerEmail);
        result = await syncGoogleCalendarBusy({
          userId: ownerEmail,
          venueId,
          days: 30,
          spaceId
        });
        if (result.needsAuth) {
          throw new Error('Could not sync after connecting Google.');
        }
      }

      if (fetchBusyTimes) {
        await fetchBusyTimes();
      }
      setLastSyncTime(new Date());
      setSyncLoading(false);
      return { count: (result.busyTimes || []).length };
    } catch (error) {
      const message = error?.message || 'Could not sync your calendar. Please try again.';
      alert(message.startsWith('Calendar connection') ? message : `Could not sync your calendar. ${message}`);
      setSyncLoading(false);
    }
  };

  return {
    syncLoading,
    lastSyncTime,
    syncCalendar,
    resolveSyncEmail
  };
};
